import { useEffect, useState } from 'react'
import { Button, Card, Col, Form, Modal, Row, Table } from 'react-bootstrap'
import { toast } from 'react-toastify'
import {
  getIncidentCategories,
  createIncidentCategory,
  updateIncidentCategory,
  deleteIncidentCategory,
} from '../../../api/IncidentAPI'

interface IncidentCategoryManagementProps {
  onTabChange: (tab: string) => void
}

const IncidentCategoryManagement: React.FC<IncidentCategoryManagementProps> = ({ onTabChange }) => {
  const [categories, setCategories] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [incidentToDelete, setIncidentToDelete] = useState<{id: string, name: string} | null>(null)

  const [showModal, setShowModal] = useState(false)
  const [editingCategory, setEditingCategory] = useState<any | null>(null)
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    slaDays: '',
  })
  const [submitLoading, setSubmitLoading] = useState(false)
  const [validationErrors, setValidationErrors] = useState<{ code?: string; name?: string }>({})

  useEffect(() => {
    fetchCategories()
  }, [pagination.page])

  const fetchCategories = async () => {
    setLoading(true)
    try {
      const response = await getIncidentCategories({
        page: pagination.page,
        limit: pagination.limit,
      })
      if (response.statusCode === 200) {
        setCategories(response.data.categories || [])
        setPagination(response.data.pagination || pagination)
      } else {
        toast.error(response.message || 'Failed to fetch incident categories')
      }
    } catch (error) {
      console.error('Error fetching incident categories:', error)
      toast.error('Failed to fetch incident categories')
    } finally {
      setLoading(false)
    }
  }

  const handleOpenModal = (category: any = null) => {
    if (category) {
      setEditingCategory(category)
      setFormData({
        code: category.code,
        name: category.name,
        description: category.description || '',
        slaDays: category.slaDays?.toString() || '',
      })
    } else {
      setEditingCategory(null)
      setFormData({
        code: '',
        name: '',
        description: '',
        slaDays: '',
      })
    }
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingCategory(null)
    setFormData({
      code: '',
      name: '',
      description: '',
      slaDays: '',
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const errors: { code?: string; name?: string } = {}

    const code = formData.code.trim()
    const name = formData.name.trim()

    if (!code) {
      errors.code = 'Category code is required.'
    }

    if (!name) {
      errors.name = 'Category name is required.'
    }

    if (formData.slaDays && (!/^[1-9]\d*$/.test(formData.slaDays) || parseInt(formData.slaDays) > 365)) {
      toast.error('SLA (Days) must be a positive whole number between 1 and 365')
      return
    }

    if (!/^[A-Za-z0-9_-]{2,20}$/.test(code)) {
      errors.code = 'Use 2-20 letters, numbers, underscores, or hyphens.'
    }
    if (name.length < 3 || name.length > 100 || !/^[\p{L}\p{N}][\p{L}\p{N} .,'()&/-]*$/u.test(name)) {
      errors.name = 'Enter 3-100 characters of readable text.'
    }
    if (Object.keys(errors).length) {
      setValidationErrors(errors)
      return
    }
    setValidationErrors({})

    setSubmitLoading(true)
    try {
      if (editingCategory) {
        const response = await updateIncidentCategory(editingCategory.id, {
          name,
          description: formData.description.trim(),
          slaDays: formData.slaDays ? parseInt(formData.slaDays) : undefined,
        })
        if (response.statusCode === 200) {
          toast.success('Incident category updated successfully')
          handleCloseModal()
          fetchCategories()
        } else {
          toast.error(response.message || 'Failed to update incident category')
        }
      } else {
        const response = await createIncidentCategory({
          code,
          name,
          description: formData.description.trim(),
          slaDays: formData.slaDays ? parseInt(formData.slaDays) : undefined,
        })
        if (response.statusCode === 201) {
          toast.success('Incident category created successfully')
          handleCloseModal()
          fetchCategories()
        } else {
          toast.error(response.message || 'Failed to create incident category')
        }
      }
    } catch (error) {
      console.error('Error saving incident category:', error)
      toast.error('Failed to save incident category')
    } finally {
      setSubmitLoading(false)
    }
  }

  const handleDelete = (id: string, name: string) => {
    setIncidentToDelete({ id, name })
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!incidentToDelete) return

    try {
      const response = await deleteIncidentCategory(incidentToDelete.id)
      if (response.statusCode === 202) {
        toast.success('Incident category deleted successfully')
        fetchCategories()
      } else {
        toast.error(response.message || `Failed to delete incident category (Status: ${response.statusCode})`)
      }
    } catch (error: any) {
      console.error('Error deleting incident category:', error)
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete incident category'
      toast.error(errorMessage)
    } finally {
      setShowDeleteModal(false)
      setIncidentToDelete(null)
    }
  }

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }))
  }

  return (
    <div className="incident-category-management">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Incident Categories</h3>
        <Button variant="success" onClick={() => handleOpenModal(null)}>
          Add Category
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : categories.length === 0 ? (
        <Card className="text-center py-5">
          <Card.Body>
            <h5 className="text-muted">No Incident Categories Found</h5>
            <p className="text-muted">Create your first incident category to get started.</p>
            <Button variant="success" onClick={() => handleOpenModal(null)}>
              Add Category
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Description</th>
                  <th>SLA (Days)</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map(category => (
                  <tr key={category.id}>
                    <td>
                      <code>{category.code}</code>
                    </td>
                    <td>
                      <strong>{category.name}</strong>
                    </td>
                    <td>{category.description || '-'}</td>
                    <td>{category.slaDays || '-'}</td>
                    <td>
                      <span className={`badge ${category.isActive ? 'bg-success' : 'bg-secondary'}`}>
                        {category.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2 flex-nowrap">
                        <Form.Check
                          type="switch"
                          id={`toggle-${category.id}`}
                          checked={category.isActive}
                          onChange={async (e) => {
                            try {
                              const response = await updateIncidentCategory(category.id, {
                                isActive: e.target.checked,
                              })
                              if (response.statusCode === 200) {
                                toast.success('Category updated successfully')
                                fetchCategories()
                              } else {
                                toast.error(response.message || 'Failed to update category')
                              }
                            } catch (error) {
                              console.error('Error updating category:', error)
                              toast.error('Failed to update category')
                            }
                          }}
                          className="mb-0"
                        />
                        <Button
                          variant="primary"
                          size="sm"
                          className="m-0"
                          onClick={() => handleOpenModal(category)}
                          disabled={!category.isActive}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          className="btn-sm"
                          onClick={() => handleDelete(category.id, category.name)}
                          disabled={!category.isActive}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {pagination.totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-4">
                <Button
                  variant="secondary"
                  disabled={pagination.page === 1}
                  onClick={() => handlePageChange(pagination.page - 1)}
                >
                  Previous
                </Button>
                <span className="text-muted">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <Button
                  variant="secondary"
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => handlePageChange(pagination.page + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* <Modal show={showModal} onHide={handleCloseModal} centered> */}
      <Modal show={showModal} onHide={handleCloseModal} centered  backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingCategory ? 'Edit Incident Category' : 'Add Incident Category'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label className="required fw-bold">Category Code</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., HARASSMENT"
                value={formData.code}
                onChange={(e) => { setFormData({ ...formData, code: e.target.value.toUpperCase() }); setValidationErrors(prev => ({ ...prev, code: undefined })) }}
                disabled={!!editingCategory}
                required
                maxLength={20}
                isInvalid={!!validationErrors.code}
              />
              {validationErrors.code && <Form.Control.Feedback type="invalid" className="d-block">{validationErrors.code}</Form.Control.Feedback>}
              <Form.Text className="text-muted">
                Letters, numbers, underscores, and hyphens (2-20 characters). Cannot be changed after creation.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="required fw-bold">Category Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Harassment"
                value={formData.name}
                onChange={(e) => { setFormData({ ...formData, name: e.target.value }); setValidationErrors(prev => ({ ...prev, name: undefined })) }}
                required
                maxLength={100}
                isInvalid={!!validationErrors.name}
              />
              <Form.Control.Feedback type="invalid">{validationErrors.name}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Description (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter category description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                maxLength={500}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">SLA (Days) (Optional)</Form.Label>
              <Form.Control
                type="number"
                placeholder="e.g., 90"
                value={formData.slaDays}
                onChange={(e) => setFormData({ ...formData, slaDays: e.target.value })}
                min="1"
                max="365"
              />
              <Form.Text className="text-muted">
                Positive whole number between 1 and 365 days.
              </Form.Text>
            </Form.Group>

            <div className="d-flex gap-2 justify-content-end">
              <Button variant="secondary" onClick={handleCloseModal} disabled={submitLoading}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={submitLoading}>
                {submitLoading ? 'Saving...' : editingCategory ? 'Update' : 'Create'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete "{incidentToDelete?.name}"? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            No
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Yes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default IncidentCategoryManagement
